import axios from 'axios';
import dayjs from 'dayjs';
import { notification } from 'antd';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SERVER_API_ENDPOINT } from 'constants/envs';
import AxiosUtils from 'utils/axios-utils';
import { formatData } from 'utils/common';
import { API } from 'service/api-route';
import { extractList } from 'pages/star-shared/utils';
import { CLASS_LABEL, TIER_RANK } from './labels';
import { FeedClass, FeedSettingsView, FeedTier, PersonRef } from './types';

// ------------------------------------------------------------------ lỗi (03 §13)

const GENERIC =
  /^(Bad Request|Not Found|Conflict|Payload Too Large|Too Many Requests|Unprocessable Entity|Forbidden|Unauthorized|Internal Server Error|Service Unavailable)$/i;

/** Câu lỗi tiếng Việt. FeedError: mã ở `message`, câu ở `error` (01 §12). Interceptor ném response.data. */
export function feedError(err: any, fallback = 'Có lỗi xảy ra, vui lòng thử lại.'): string {
  if (err?.networkError) return 'Không kết nối được máy chủ.';
  const code = typeof err?.message === 'string' ? err.message : '';
  const status = err?.statusCode || err?.status;
  if (code === 'FEED_UNAVAILABLE') {
    return 'Máy chủ chưa có bảng dữ liệu Bảng tin — cần chạy migration Bảng tin (feed).';
  }
  if (typeof err?.error === 'string' && err.error && !GENERIC.test(err.error)) {
    if (code === 'FEED_BANNED_WORD' && Array.isArray(err.words)) {
      return `${err.error} (${err.words.join(', ')})`;
    }
    return err.error;
  }
  if (Array.isArray(err?.message)) return err.message.join('; ');
  if (status === 404) return 'Không tìm thấy (nội dung đã bị xoá hoặc máy chủ chưa có Bảng tin).';
  if (status === 403) return 'Phiên đăng nhập đã hết. Tải lại trang để đăng nhập lại.';
  if (status === 413) return 'Tệp quá lớn.';
  if (status >= 500) return 'Lỗi máy chủ, vui lòng thử lại sau.';
  if (code && !/^[A-Z_]+$/.test(code)) return code;
  return fallback;
}

export const errorCode = (err: any): string =>
  typeof err?.message === 'string' ? err.message : '';

export const errorStatus = (err: any): number | undefined => err?.statusCode || err?.status;

// ------------------------------------------------------------------ tải file

/** Đường dẫn API tương đối (`/feed/...`) hoặc `/api/...` → URL tuyệt đối tới backend. */
export function resolveApiUrl(url: string): string {
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  const base = (SERVER_API_ENDPOINT || '').replace(/\/+$/, '');
  if (url.startsWith('/api/')) return base.replace(/\/api$/, '') + url;
  return base + (url.startsWith('/') ? url : `/${url}`);
}

/** Link file private của D39 — phải tải bằng axios (cookie phiên), <img src> thường bị 403/404. */
export const isAdminMediaUrl = (url?: string | null) =>
  !!url && /\/feed\/admin\/media\/\d+\/file/.test(url);

export const adminMediaUrl = (id: number, v: 'original' | 'preview') =>
  `${API.feedAdminMediaFile}/${id}/file?v=${v}`;

/**
 * Tải ảnh/video trong hộp đăng bài (và "Thử một ảnh") — cần thanh tiến độ và thời gian chờ dài,
 * nên gọi thẳng axios. Lỗi 403 đi đường này không qua interceptor, feedError báo "Phiên… hết".
 */
export async function uploadFeedMedia(
  url: string,
  form: FormData,
  onProgress?: (pct: number) => void
): Promise<any> {
  try {
    const res = await axios.post(url, form, {
      baseURL: SERVER_API_ENDPOINT,
      withCredentials: true,
      timeout: 15 * 60 * 1000,
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (e.total) onProgress?.(Math.round((e.loaded * 100) / e.total));
      },
    });
    return res.data;
  } catch (e: any) {
    throw e?.response?.data ?? { message: 'Không kết nối được máy chủ.', networkError: true };
  }
}

/** D39: tải file private thành blob URL. Nơi gọi phải URL.revokeObjectURL khi xong. */
export async function fetchMediaObjectUrl(url: string): Promise<string> {
  const res = await axios.get(resolveApiUrl(url), {
    withCredentials: true,
    responseType: 'blob',
    timeout: 5 * 60 * 1000,
  });
  return URL.createObjectURL(res.data as Blob);
}

// ------------------------------------------------------------------ danh sách

interface ListOptions {
  refreshKey?: number;
  enabled?: boolean;
}

/**
 * Như pages/star-shared/use-paged-list nhưng báo lỗi bằng feedError (câu của backend Bảng tin):
 * usePagedList đổi 404 thành "API Điểm Sao chưa sẵn sàng", sai ngữ cảnh ở đây.
 */
export function useFeedList<T>(url: string, params: Record<string, any>, options: ListOptions = {}) {
  const { refreshKey = 0, enabled = true } = options;
  const [items, setItems] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const paramsKey = JSON.stringify(params);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setLoading(true);
    AxiosUtils.get(url, formatData({ ...JSON.parse(paramsKey) }))
      .then((res: any) => {
        if (cancelled) return;
        const next = extractList<T>(res);
        setItems(next.items);
        setTotal(next.total);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        const text = feedError(err);
        setError(text);
        notification.error({ message: text });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [url, paramsKey, refreshKey, enabled, tick]);

  const reload = useCallback(() => setTick((t) => t + 1), []);
  return { items, total, loading, error, reload, setItems };
}

/** Bộ lọc nằm trên URL (gửi link được, F5 vẫn giữ). Đổi lọc thì về trang 1. */
export function useUrlFilters() {
  const [params, setParams] = useSearchParams();
  const get = (key: string) => params.get(key) || undefined;
  const set = (patch: Record<string, string | number | boolean | null | undefined>) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        Object.keys(patch).forEach((key) => {
          const value = patch[key];
          if (value === undefined || value === null || value === '' || value === false) next.delete(key);
          else next.set(key, value === true ? '1' : String(value));
        });
        if (!('page' in patch)) next.delete('page');
        return next;
      },
      { replace: true }
    );
  };
  /**
   * Xoá mọi tham số trừ `keep` (mặc định giữ tab và ngăn chi tiết đang mở), rồi đặt `extra`.
   * Làm trong MỘT lần setParams — gọi clear() rồi set() liền nhau thì lần sau đọc URL cũ.
   */
  const clear = (keep: string[] = ['tab', 'post'], extra?: Record<string, string>) => {
    setParams(
      (prev) => {
        const next = new URLSearchParams();
        keep.forEach((key) => {
          const value = prev.get(key);
          if (value) next.set(key, value);
        });
        if (extra) Object.keys(extra).forEach((key) => next.set(key, extra[key]));
        return next;
      },
      { replace: true }
    );
  };
  return { params, get, set, clear };
}

// ------------------------------------------------------------------ cài đặt (cache nhẹ)

let settingsCache: Promise<FeedSettingsView | null> | null = null;

/** D21 — hộp đăng bài / Quan trọng / Cần xem chỉ cần đọc vài giá trị, tải một lần. */
export function loadFeedSettings(force = false): Promise<FeedSettingsView | null> {
  if (!settingsCache || force) {
    settingsCache = AxiosUtils.get(API.feedAdminSettings)
      .then((res: any) => (res?.data as FeedSettingsView) || null)
      .catch(() => {
        settingsCache = null;
        return null;
      });
  }
  return settingsCache;
}

export function setFeedSettingsCache(value: FeedSettingsView) {
  settingsCache = Promise.resolve(value);
}

export function useFeedSettingsOnce(enabled = true): FeedSettingsView | null {
  const [value, setValue] = useState<FeedSettingsView | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    loadFeedSettings().then((v) => alive && setValue(v));
    return () => {
      alive = false;
    };
  }, [enabled]);
  return value;
}

// ------------------------------------------------------------------ người, bậc, định dạng

export const rankOf = (tier?: FeedTier | null) => (tier ? TIER_RANK[tier] || 0 : 0);

/**
 * Nhãn lớp theo cách gọi của chủ dự án. Backend có thể gửi `classLabel` chi tiết hơn
 * (vd. "Creator NPC") thì dùng; nhãn cũ "NPC"/"Idol" đổi sang "Nhân viên"/"Creator".
 */
export function classText(userClass?: FeedClass | string | null, classLabel?: string | null): string {
  const label = (classLabel || '').trim();
  if (label && label !== 'NPC' && !/^idol$/i.test(label)) return label;
  if (/^idol$/i.test(label)) return 'Creator';
  if (userClass && userClass in CLASS_LABEL) return CLASS_LABEL[userClass as FeedClass];
  return label === 'NPC' ? 'Nhân viên' : label;
}

export function personText(person?: PersonRef | null, fallbackId?: number | null): string {
  if (person?.name) return person.name;
  if (person?.username) return person.username;
  const id = person?.userId ?? fallbackId;
  return id ? `#${id}` : '—';
}

export const fmtDateTime = (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY HH:mm') : '—');
export const fmtDate = (v?: string | null) => (v ? dayjs(v).format('DD/MM/YYYY') : '—');
export const fmtTime = (v?: string | null, seconds = false) =>
  v ? dayjs(v).format(seconds ? 'HH:mm:ss' : 'HH:mm') : '';
export const fmtShort = (v?: string | null) => (v ? dayjs(v).format('DD/MM HH:mm') : '');

const decimal1 = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 1 });
const integer = new Intl.NumberFormat('vi-VN');

export const fmtNumber = (v?: number | null) => (v === null || v === undefined ? '—' : integer.format(v));

/** 12,4 MB · 850 KB · 1,2 GB */
export function fmtBytes(bytes?: number | null): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${decimal1.format(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${decimal1.format(bytes / (1024 * 1024))} MB`;
  return `${decimal1.format(bytes / (1024 * 1024 * 1024))} GB`;
}

export const fmtScore = (v?: number | null) =>
  v === null || v === undefined ? '' : Number(v).toFixed(2).replace('.', ',');

/** Chuỗi JSON thì giải, lỗi thì trả nguyên (03 §12: đọc dễ dãi). */
export function parseLoose<T = unknown>(value: unknown): T | unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

export function toStringList(value: unknown): string[] {
  const parsed = parseLoose(value);
  if (Array.isArray(parsed)) return parsed.map((x) => String(x));
  if (typeof parsed === 'string' && parsed) return parsed.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
}

/** clientRef 8–64 ký tự [A-Za-z0-9_-] — một hộp đăng bài dùng đúng một giá trị (01 §5.3). */
export function makeClientRef(): string {
  const c: any = typeof window !== 'undefined' ? (window as any).crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return String(c.randomUUID());
  return (Date.now().toString(36) + Math.random().toString(36).slice(2)).replace(/[^A-Za-z0-9_-]/g, '');
}

/** Đọc thời lượng video trên máy (giây). Trình duyệt không đọc được → null (để máy chủ kiểm). */
export function readVideoDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    let url = '';
    try {
      url = URL.createObjectURL(file);
    } catch {
      resolve(null);
      return;
    }
    const video = document.createElement('video');
    const done = (value: number | null) => {
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timer = window.setTimeout(() => done(null), 10000);
    video.preload = 'metadata';
    video.onloadedmetadata = () => done(Number.isFinite(video.duration) ? video.duration : null);
    video.onerror = () => done(null);
    video.src = url;
  });
}

// ------------------------------------------------------------------ từ cấm

/** Trùng cách backend chuẩn hoá `term` (01 §6 cuối: NFC + chữ thường 'vi' + gộp khoảng trắng + trim). */
export const normalizeTerm = (raw: string) =>
  raw.normalize('NFC').toLocaleLowerCase('vi').replace(/\s+/g, ' ').trim();

/** Bỏ dấu tiếng Việt (đ → d). */
export const stripAccents = (text: string) =>
  text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D');

/** Từ MỘT chữ có dấu mà bật "không dấu" dễ bắt nhầm (vd. “cặc” → “các”). */
export function riskyAsciiTerm(term: string): boolean {
  const t = normalizeTerm(term);
  return !!t && !t.includes(' ') && stripAccents(t) !== t;
}
